/**
 * The signed-in user's navigation destinations, with their gating.
 *
 * Currently consumed by the Profile Settings left rail
 * (components/Settings/SettingsNav.jsx). It mirrors the navbar user dropdown
 * (components/Navbar/navbar.jsx) item-for-item — same paths, same t() keys,
 * same beta/business gates — so the rail always reflects what the user can
 * actually reach.
 *
 * TODO(navbar): navbar.jsx still declares these inline, so the two can drift.
 * The user dropdown now has a single charities-beta "My Charities" item, while
 * this rail still lists the full charity group. Adopting this module in the
 * navbar needs its own change and verification.
 *
 * Groups render only when they contain at least one visible item, so callers
 * don't need to reason about gating when laying out dividers.
 */

export const HELP_URLS = {
  en: 'https://civicrush.com/help-and-support/',
  es: 'https://civicrush.com/ayuda/',
}

export const FAQ_URLS = {
  en: 'https://civicrush.com/faq/',
  es: 'https://civicrush.com/faq/',
}

/**
 * True when this rail item should show as the current page.
 *
 * Exact `to` matches cover hash destinations (e.g. `/settings#wallet`).
 * `matchPrefix` is for parent routes with nested screens (`/charities/register`,
 * `/charity-account/:id`) so the rail stays highlighted inside that flow.
 * It is opt-in: `/profile` must not match `/profile/charity`.
 */
export function isUserMenuItemActive(item, pathname, currentPath) {
  if (!item?.to) return false
  if (item.to === currentPath || item.to === pathname) return true
  if (!item.matchPrefix) return false
  const pathOnly = item.to.split('#')[0]
  return Boolean(pathOnly) && pathname.startsWith(`${pathOnly}/`)
}

/**
 * @returns {Array<{id: string, items: Array<{
 *   id: string, to?: string, href?: string, external?: boolean,
 *   icon: string, label: string, action?: string, matchPrefix?: boolean
 * }>}>}
 */
export function getUserMenuItems({ currentUser, hasBetaFeature, locale, t }) {
  const isEs = locale === 'es'
  const hasBusiness = !!(currentUser?.has_business && currentUser?.business_id)
  const profilePath = currentUser?.nickname
    ? `/profile/${encodeURIComponent(currentUser.nickname)}`
    : '/profile'

  const groups = [
    {
      id: 'account',
      items: [
        {
          id: 'settings',
          to: '/settings',
          icon: 'gear',
          label: t('navigation_banner.my_settings'),
        },
        {
          id: 'profile',
          to: profilePath,
          icon: 'circle-user-solid',
          label: t('navigation_banner.profile', 'Profile'),
        },
        hasBetaFeature('civ_wallet') && {
          id: 'wallet',
          to: '/settings#wallet',
          icon: 'wallet',
          label: t('navigation_banner.civ_wallet', 'CIV Wallet'),
        },
      ],
    },
    {
      id: 'activity',
      items: [
        {
          id: 'civic-participation',
          to: '/civic-participation',
          icon: 'chart-line-up',
          label: t('navigation_banner.civic_participation_link'),
        },
        {
          id: 'my-posts',
          to: '/my-posts',
          icon: 'calendar-days',
          label: t('navigation_banner.my_posts'),
        },
        {
          id: 'engagements',
          to: '/engagements',
          icon: 'hand-peace',
          label: t('navigation_banner.engagements'),
        },
        {
          id: 'groups',
          to: '/groups',
          icon: 'users',
          label: t('navigation_banner.groups'),
        },
        hasBetaFeature('pf_organizer') && {
          id: 'organizer-dashboard',
          to: '/dashboard-organizer',
          icon: 'calendar-check',
          label: t('navigation_banner.organizer_dashboard'),
        },
      ],
    },
    {
      id: 'business',
      items: hasBusiness
        ? [
            {
              id: 'business-account',
              to: '/business-account',
              icon: 'building',
              label: t('navigation_banner.business_account'),
            },
            {
              id: 'business-profile',
              to: '/profile/business',
              icon: 'handshake',
              label: t(
                'navigation_banner.business_profile',
                'Business Profile',
              ),
            },
            hasBetaFeature('pf_business') && {
              id: 'business-dashboard',
              to: '/dashboard',
              icon: 'grid-2',
              label: t('navigation_banner.business_dashboard'),
            },
          ]
        : [],
    },
    {
      id: 'charity',
      items: hasBetaFeature('charities')
        ? [
            {
              id: 'my-charities',
              to: '/charities',
              matchPrefix: true,
              icon: 'hands-heart',
              label: t('navigation_banner.my_charities'),
            },
            {
              id: 'charity-account',
              to: '/charity-account',
              matchPrefix: true,
              icon: 'gear',
              label: t('navigation_banner.charity_account'),
            },
            {
              id: 'charity-profile',
              to: '/profile/charity',
              icon: 'hands-heart',
              label: t('navigation_banner.charity_profile', 'Charity Profile'),
            },
            {
              id: 'charity-dashboard',
              to: '/dashboard-charity',
              icon: 'hands-usd',
              label: t('navigation_banner.my_charities_dashboard'),
            },
          ]
        : [],
    },
    {
      id: 'support',
      items: [
        {
          id: 'help',
          href: isEs ? HELP_URLS.es : HELP_URLS.en,
          external: true,
          icon: 'life-ring',
          label: t('navigation_banner.help'),
        },
        {
          id: 'faqs',
          href: isEs ? FAQ_URLS.es : FAQ_URLS.en,
          external: true,
          icon: 'question',
          label: 'FAQs',
        },
        {
          id: 'contact',
          to: '/contact-us',
          icon: 'envelope',
          label: t('navigation_banner.contact_link'),
        },
        {
          id: 'legal',
          action: 'legal',
          icon: 'file-lines',
          label: t('settings.nav.legal', 'Legal'),
        },
        {
          id: 'logout',
          to: '/logout',
          icon: 'sign-out-alt',
          label: t('devise.sessions.logout'),
        },
      ],
    },
  ]

  return groups
    .map(group => ({ ...group, items: group.items.filter(Boolean) }))
    .filter(group => group.items.length > 0)
}

export default getUserMenuItems
